import React from 'react';
import { useTranslation } from 'react-i18next';
import { generateEpisodeJSONLD } from '@jwp/ott-common/src/utils/structuredData';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useTranslationKey } from '@jwp/ott-hooks-react/src/useTranslationKey';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { countListValues, createVideoFormatMetadata } from '@jwp/ott-common/src/utils/metadata';
import PlayTrailer from '@jwp/ott-theme/assets/icons/play_trailer.svg?react';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useSeriesData from '@jwp/ott-hooks-react/src/useSeriesData';
import useAutoFocus from '@jwp/ott-ui-react/src/hooks/useAutoFocus';
import { useAutoScroll } from '@jwp/ott-ui-react/src/hooks/useAutoScroll';
import useSeriesParams from '@jwp/ott-ui-react/src/hooks/useSeriesParams';
import useSeriesHandlers from '@jwp/ott-ui-react/src/hooks/useSeriesHandlers';
import type { ScreenComponent } from '@jwp/ott-ui-react/types/screens';
import Cinema from '@jwp/ott-ui-react/src/containers/Cinema/Cinema';
import TrailerModal from '@jwp/ott-ui-react/src/containers/TrailerModal/TrailerModal';
import ShareButton from '@jwp/ott-ui-react/src/components/ShareButton/ShareButton';
import FavoriteButton from '@jwp/ott-ui-react/src/containers/FavoriteButton/FavoriteButton';
import Button from '@jwp/ott-ui-react/src/components/Button/Button';
import Icon from '@jwp/ott-ui-react/src/components/Icon/Icon';
import VideoMetaData from '@jwp/ott-ui-react/src/components/VideoMetaData/VideoMetaData';
import VideoTags from '@jwp/ott-ui-react/src/components/VideoTags/VideoTags';
import VideoDefinitionList from '@jwp/ott-ui-react/src/components/VideoDefintionsList/VideoDefinitionList';
import MediaHead from '@jwp/ott-ui-react/src/components/MediaHead/MediaHead';
import MediaHeroInfo from '@jwp/ott-ui-react/src/components/MediaHeroInfo/MediaHeroInfo';
import MediaHeroButtons from '@jwp/ott-ui-react/src/components/MediaHeroButtons/MediaHeroButtons';
import MediaHero from '@jwp/ott-ui-react/src/components/MediaHero/MediaHero';
import MediaTabs from '@jwp/ott-ui-react/src/components/MediaTabs/MediaTabs';
import SeasonSelector from '@jwp/ott-ui-react/src/components/SeasonSelector/SeasonSelector';
import MarkdownComponent from '@jwp/ott-ui-react/src/components/MarkdownComponent/MarkdownComponent';
import MediaTabAbout from '@jwp/ott-ui-react/src/components/MediaTabAbout/MediaTabAbout';
import StartWatchingButton from '@jwp/ott-ui-react/src/containers/StartWatchingButton/StartWatchingButton';
import ErrorPage from '@jwp/ott-ui-react/src/components/ErrorPage/ErrorPage';
import { formatSeriesMetaString } from '@jwp/ott-common/src/utils/formatting';
import CardGrid from '@jwp/ott-ui-react/src/components/CardGrid/CardGrid';
import Fade from '@jwp/ott-ui-react/src/components/Animation/Fade/Fade';
import EpisodeList from '@jwp/ott-ui-react/src/components/EpisodeList/EpisodeList';
import Loading from '@jwp/ott-ui-react/src/pages/Loading/Loading';
import { getItemYear } from '@jwp/ott-common/src/utils/media';
import styles from '@jwp/ott-ui-react/src/pages/ScreenRouting/mediaScreens/MediaMovie/MediaMovie.module.scss';
import env from '@jwp/ott-common/src/env';

import TruncatedText from '../../../../components/TruncatedText/TruncatedText';

const MediaSeriesVideodock: ScreenComponent<PlaylistItem> = ({ data }) => {
  const { t } = useTranslation('video');
  const translationKey = useTranslationKey('title');
  const breakpoint = useBreakpoint();
  const { seriesId, play, feedId, episodeId: episodeIdParam, searchParams, setSearchParams } = useSeriesParams();
  const {
    series,
    episode,
    episodeMetadata,
    initialEpisodeId,
    isFirstEpisode,
    filters,
    season,
    playEpisode,
    episodesPlaylist,
    recommendations,
    accessModel,
    nextItem,
    playTrailer,
    trailerItem,
    primaryMetadata,
    isRecommendationsLoading,
    isLoading,
    isEpisodesLoading,
    isEpisodesFetching,
    isEntitled,
    areEpisodesEntitled,
    isFavoritesEnabled,
    isLoggedIn,
    passes,
    hasTrailer,
    hasSubscription,
    hasRecommendations,
    watchHistory,
    hasNextEpisodesPage,
    videoFormats,
    fetchNextEpisodes,
    getCardSeriesUrl,
    setPlayTrailer,
    setActiveSeason,
    setEpisodeId,
  } = useSeriesData(data, seriesId, episodeIdParam);
  const { handlePlay, handlePlayEpisode, handleBack, handleComplete } = useSeriesHandlers(
    setSearchParams,
    searchParams,
    nextItem,
    setEpisodeId,
    areEpisodesEntitled,
    feedId,
  );

  const structuredData = series && generateEpisodeJSONLD(series, data, env.APP_PUBLIC_URL, initialEpisodeId ? episode : undefined, episodeMetadata);
  const canonicalUrl = data ? `${env.APP_PUBLIC_URL}${mediaURL({ id: data.mediaid, title: data.title })}` : window.location.href;
  const recommendationsTabTitle = hasRecommendations ? (recommendations?.[translationKey] as string) || recommendations?.title || '' : '';

  const smallScreen = breakpoint < Breakpoint.md;
  const trailerButton = hasTrailer ? (
    <Button
      label={t('video:trailer')}
      aria-label={t('video:watch_trailer')}
      startIcon={<Icon icon={PlayTrailer} />}
      onClick={() => setPlayTrailer(true)}
      active={playTrailer}
      disabled={!trailerItem}
    />
  ) : undefined;

  const secondaryMetadata = episodeMetadata && episode && (
    <>
      <strong>{formatSeriesMetaString(episodeMetadata.seasonNumber, episodeMetadata.episodeNumber)}</strong> - {episode.title}
    </>
  );

  useAutoFocus(episode?.mediaid);
  useAutoScroll(episode?.mediaid); // scroll when the episode changes

  if (isLoading) return <Loading />;

  if (!data || !series || !playEpisode) return <ErrorPage title={t('series_error')} />;

  const renderDescription = () => {
    if (initialEpisodeId && episode?.description) {
      return <TruncatedText text={episode.description} maximumLines={2} className={styles.description} />;
    }
    if (data.synopsis) {
      return data.synopsis as string;
    }
    return <TruncatedText text={data.description} maximumLines={2} className={styles.description} />;
  };

  return (
    <React.Fragment>
      <MediaHead canonicalUrl={canonicalUrl} data={data} structuredData={structuredData} />
      <MediaHero image={data.backgroundImage}>
        <MediaHeroInfo
          title={data.title}
          subtitle={initialEpisodeId ? secondaryMetadata : undefined}
          description={renderDescription()}
          metadataComponent={<VideoMetaData attributes={primaryMetadata} />}
          tagsComponent={<VideoTags attributes={videoFormats} />}
          definitionComponent={
            <VideoDefinitionList
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.actors', { count: countListValues(data?.actors as string) }), value: data?.actors as string },
                { label: t('video:metadata.directors', { count: countListValues(data?.directors as string) }), value: data?.directors as string },
              ]}
            />
          }
        />
        <MediaHeroButtons
          cta={
            <>
              <StartWatchingButton
                key={seriesId} // necessary to fix autofocus on TalkBack
                item={playEpisode}
                episodeMetadata={episodeMetadata}
                isSequentialEpisode={!isFirstEpisode}
                onClick={handlePlay}
              />
              {!smallScreen && trailerButton}
            </>
          }
        >
          {smallScreen && trailerButton}
          {isFavoritesEnabled ? <FavoriteButton item={data} /> : undefined}
          <ShareButton title={data.title} description={data.description} url={canonicalUrl} />
        </MediaHeroButtons>
      </MediaHero>
      <MediaTabs
        titles={[t('video:episodes'), recommendationsTabTitle, t('video:about_tab')]}
        isLoading={isLoading || isEpisodesLoading || isRecommendationsLoading}
      >
        {episodesPlaylist && (
          <Fade open={!isEpisodesFetching} duration={500}>
            {filters.length > 0 && (
              <SeasonSelector
                isLoading={isEpisodesFetching}
                value={season.value}
                year={season.year}
                description={season.description}
                options={filters}
                onChange={(value) => setActiveSeason(value)}
              />
            )}
            <EpisodeList
              areEpisodesEntitled={areEpisodesEntitled}
              playlist={episodesPlaylist}
              isLoading={isLoading || isEpisodesFetching}
              hasMore={hasNextEpisodesPage}
              loadMore={fetchNextEpisodes}
              watchHistory={watchHistory}
              onEpisodeClick={handlePlayEpisode}
              renderEpisodeMetadata={(item) => (
                <>
                  <VideoTags attributes={createVideoFormatMetadata(item)} />
                  {getItemYear(item) ? <VideoMetaData attributes={[getItemYear(item)?.toString() as string]} /> : undefined}
                </>
              )}
            />
          </Fade>
        )}
        {recommendations && (
          <CardGrid
            playlist={recommendations}
            isLoading={isLoading || isRecommendationsLoading}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            hasSubscription={hasSubscription}
            passes={passes}
            getUrl={getCardSeriesUrl}
          />
        )}
        <MediaTabAbout description={<MarkdownComponent className={styles.markdown} markdownString={data.description} headerOffset={1} />}>
          <div className={styles.aboutDefinitionList}>
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.directors', { count: countListValues(data?.directors as string) }), value: data?.directors as string },
                { label: t('video:metadata.producers', { count: countListValues(data?.producers as string) }), value: data?.producers as string },
                { label: t('video:metadata.creators', { count: countListValues(data?.creators as string) }), value: data?.creators as string },
                { label: t('video:metadata.cast'), value: data?.cast as string },
                { label: t('video:metadata.formats'), value: videoFormats.length ? <VideoTags attributes={videoFormats} /> : null },
              ]}
            />
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.genres', { count: countListValues(data?.genre as string) }), value: data?.genre as string },
                { label: t('video:metadata.audio_languages'), value: data?.audioLanguages as string },
                { label: t('video:metadata.subtitle_languages'), value: data?.subtitleLanguages as string },
              ]}
            />
          </div>
        </MediaTabAbout>
      </MediaTabs>
      <Cinema
        open={!isLoading && play && isEntitled}
        onClose={handleBack}
        item={playEpisode}
        seriesItem={data}
        title={data.title}
        primaryMetadata={<VideoMetaData attributes={primaryMetadata} />}
        secondaryMetadata={secondaryMetadata}
        onComplete={handleComplete}
        feedId={feedId ?? undefined}
      />
      <TrailerModal item={trailerItem} title={`${data.title} - Trailer`} open={playTrailer} onClose={() => setPlayTrailer(false)} />
    </React.Fragment>
  );
};

export default MediaSeriesVideodock;
